import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import { PrintDocumentMenuOptions } from 'features/knowledgeDocumentViewer/components/toolbar/PrintDocumentMenuOptions'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { useDocumentPrintActions } from 'features/knowledgeDocumentViewer/hooks/useDocumentPrintActions'
import { useViewerCantileverPageImageRef } from 'features/knowledgeDocumentViewer/hooks/useViewerCantileverPageImageRef'
import { useRolodexSignedURLs } from 'features/rolodex'
import { Loader2, Printer } from 'lucide-react'
import { type ComponentProps, useState } from 'react'
import styled, { keyframes } from 'styled-components'

export function PrintDocumentButton() {
  const { documentSignedURL, documentType, totalPages } =
    useDocumentTabDataContext()
  const { currentPageNumber } = useKnowledgeDocumentUXContext()
  const currentPageHiResRef = useViewerCantileverPageImageRef(
    currentPageNumber,
    'hiRes'
  )
  const { url: currentPageHiResUrl } = useRolodexSignedURLs(currentPageHiResRef)
  return (
    <DocumentPrintAction
      signedUrl={documentSignedURL?.signed_url}
      documentType={documentType}
      totalPages={totalPages}
      currentPageNumber={currentPageNumber}
      currentPageImageUrl={currentPageHiResUrl}
    />
  )
}

export function DocumentPrintAction({
  signedUrl,
  documentType,
  totalPages = 1,
  currentPageNumber = 1,
  currentPageImageUrl,
  size = 'icon-lg',
}: {
  signedUrl?: string
  documentType?: string
  totalPages?: number
  currentPageNumber?: number
  currentPageImageUrl?: string
  size?: ComponentProps<typeof Button>['size']
}) {
  const { t } = useLingui()
  const [menuOpen, setMenuOpen] = useState(false)
  const showPageMenu = totalPages > 1
  const {
    isPreparing,
    handleCurrentPagePrint,
    handleRangePrint,
    handleFullDocumentPrint,
  } = useDocumentPrintActions({
    pdfSignedUrl: signedUrl,
    documentType,
    currentPageNumber,
    currentPageImageUrl,
  })

  const onCurrentPage = () => {
    setMenuOpen(false)
    handleCurrentPagePrint()
  }

  const onRange = (startPage: number, endPage: number) => {
    setMenuOpen(false)
    handleRangePrint(startPage, endPage)
  }

  const onFullDocument = () => {
    setMenuOpen(false)
    handleFullDocumentPrint()
  }

  const icon = isPreparing ? (
    <SpinningLoader size={18} strokeWidth={1.5} />
  ) : (
    <Printer size={18} strokeWidth={1.5} />
  )

  const buttonTitle = isPreparing ? t`Preparing print...` : t`Print Document`
  const isDisabled = !signedUrl || isPreparing

  if (!showPageMenu) {
    return (
      <Button
        variant='inputRaised'
        size={size}
        title={buttonTitle}
        onClick={onCurrentPage}
        disabled={isDisabled}>
        {icon}
      </Button>
    )
  }

  return (
    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='inputRaised'
          size={size}
          title={buttonTitle}
          disabled={isDisabled}>
          {icon}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <PrintDocumentMenuOptions
          totalPages={totalPages}
          currentPageNumber={currentPageNumber}
          pdfSignedUrl={signedUrl}
          isPreparing={isPreparing}
          onCurrentPage={onCurrentPage}
          onFullDocument={onFullDocument}
          onRange={onRange}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`

const SpinningLoader = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`
